import { MouseEvent, useEffect } from 'react';

import {
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import { BlockedContactName } from 'components/BlockedContactName';
import DialogHeader from 'components/DialogHeader';
import ImageViewer from 'components/ImageViewer';
import ModalEditarAgendamento from 'components/ModalEditarAgendamento';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AgendamentoInterfaceV2,
  AgendamentoStatusEnum,
  cancelarAgendamento,
  fetchAgendamentosV2,
  resetAgendamentosV2,
  resetIdsAgendamentosSelecionados,
} from 'store/reducers/agendamentos';
import {
  ContatoInterface,
  fetchContatoAtendimentoV2,
} from 'store/reducers/contatos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import { getLabel } from './AgendamentoListItem';

const useStyles = makeStyles(({ typography, palette, spacing }) => ({
  dialogContent: {
    paddingInline: spacing(2),
    paddingBlock: spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
  },
  card: {
    backgroundColor: alpha(palette.primary.main, 0.05),
    borderRadius: spacing(1),
    padding: spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(0.5),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(0.5),
  },
  sectionTitle: {
    color: alpha(palette.primary.main, 0.7),
    fontWeight: 600,
    fontSize: typography.pxToRem(14),
  },
  contactContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  statusBadgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusBadgeColor: ({ color }: { color: string }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: color,
      transform: 'scale(0.5) translate(0, 0)',
    },
  }),
  observationBox: {
    padding: spacing(1),
    backgroundColor: alpha(palette.error.main, 0.05),
    borderRadius: spacing(1),
  },
  observationTitle: {
    color: alpha(palette.error.main, 0.9),
    fontWeight: 500,
    fontSize: typography.pxToRem(14),
    marginBottom: spacing(1),
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: spacing(2),
  },
  dialogActionsWithoutCancel: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: spacing(1, 2),
  },
  actionsRight: {
    display: 'flex',
    gap: spacing(1),
  },
  confirmPaper: {
    '& .MuiDialogTitle-root .MuiTypography-h6': {
      fontSize: typography.pxToRem(14),
      fontWeight: typography.fontWeightMedium as number,
    },
    '& .MuiDialogContent-root .MuiDialogContentText-root': {
      fontSize: typography.pxToRem(14),
    },
  },
  textPrimary: {
    fontWeight: 500,
    fontSize: typography.pxToRem(14),
  },
  textSecondary: {
    variant: 'caption',
  },
  morePaddingBottom: {
    paddingBottom: spacing(3),
  },
}));

interface ModalDetalhesAgendamentoProps {
  agendamento: AgendamentoInterfaceV2;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ModalDetalhesAgendamento({
  agendamento,
  open,
  onClose,
  onSuccess,
}: ModalDetalhesAgendamentoProps) {
  const theme = useTheme();
  const confirm = useConfirm();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();

  const contactFromStore = useAppSelector(
    state => state.contatos.contatoAtendimentoV2,
  );

  const { label, color } = getLabel(agendamento.status, theme);
  const classes = useStyles({ color });

  useEffect(() => {
    if (open && agendamento.contact.id) {
      dispatch(fetchContatoAtendimentoV2(agendamento.contact.id));
    }
  }, [agendamento.contact.id, dispatch, open]);

  const displayContact = {
    name: contactFromStore?.name || agendamento.contact.name,
    number: contactFromStore?.contact || agendamento.contact.number,
    photoUrl: contactFromStore?.fotoUrl || agendamento.contact.photoUrl,
    blockedAt: contactFromStore?.blockedAt || agendamento.contact.blockedAt,
  };

  const [showModalEditarAgendamento, hideModalEditarAgendamento] = useModal(
    ({ in: open }) => (
      <ModalEditarAgendamento
        open={open}
        onClose={hideModalEditarAgendamento}
        agendamento={agendamento}
        contato={
          {
            ...displayContact,
            nome: displayContact.name,
            contato: displayContact.number,
            foto: displayContact.photoUrl || '',
          } as unknown as ContatoInterface
        }
        onSuccess={onSuccess}
      />
    ),
    [agendamento],
  );

  const handleCancel = (
    e: MouseEvent<HTMLButtonElement>,
    agendamentoId: string,
  ) => {
    e.stopPropagation();

    confirm({
      title: 'Cancelar agendamento',
      description: 'Deseja realmente cancelar este agendamento?',
      cancellationText: 'Não',
      confirmationText: 'Sim',
      dialogProps: { maxWidth: 'xs', classes: { paper: classes.confirmPaper } },
    })
      .then(async () => {
        await dispatch(cancelarAgendamento(agendamentoId))
          .unwrap()
          .then(() => {
            enqueueSnackbar(
              'O agendamento foi cancelado e não está mais disponível para você.',
            );

            if (currentUser) {
              dispatch(resetAgendamentosV2());
              dispatch(
                fetchAgendamentosV2({
                  attendantId: currentUser.id,
                }),
              );
            }

            dispatch(resetIdsAgendamentosSelecionados());
            onSuccess?.();
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      })
      .catch(() => undefined);
  };

  const handleEditarAgendamento = () => {
    onClose();
    showModalEditarAgendamento();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogHeader title="Detalhes do agendamento" onClose={onClose} />

      <DialogContent
        className={clsx(classes.dialogContent, {
          [classes.morePaddingBottom]:
            agendamento.status === AgendamentoStatusEnum.CANCELADO,
        })}
      >
        <Box className={classes.card}>
          <Box className={classes.contactContainer}>
            <ListItemAvatar>
              <ImageViewer src={displayContact.photoUrl || ''}>
                <Avatar src={displayContact.photoUrl || ''} />
              </ImageViewer>
            </ListItemAvatar>

            <ListItemText
              primary={
                <BlockedContactName
                  name={displayContact.name}
                  isBlocked={Boolean(displayContact.blockedAt)}
                  variant="body2"
                  className={classes.textPrimary}
                />
              }
              secondary={displayContact.number}
              primaryTypographyProps={
                { component: 'div' } as Record<string, unknown>
              }
              secondaryTypographyProps={{
                variant: 'caption',
                noWrap: true,
                title: displayContact.name,
              }}
              style={{ margin: 0 }}
            />
          </Box>

          <ListItemText
            primary="Mensagem"
            secondary={agendamento.message}
            primaryTypographyProps={{
              variant: 'caption',
              color: 'textSecondary',
            }}
            secondaryTypographyProps={{
              color: 'textPrimary',
              title: agendamento.message,
            }}
            style={{ margin: 0 }}
          />
        </Box>

        <Box className={classes.section}>
          <Typography
            style={{ marginBottom: 6 }}
            variant="body2"
            className={classes.sectionTitle}
          >
            Agendamento
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <ListItemText
                primary={
                  <Box className={classes.statusBadgeContainer}>
                    <Box width={10} height={10}>
                      <Tooltip title={label} arrow>
                        <Badge
                          overlap="circular"
                          badgeContent=""
                          classes={{
                            root: clsx(classes.statusBadge, {
                              [classes.statusBadgeColor]: color,
                            }),
                          }}
                        />
                      </Tooltip>
                    </Box>
                    Data e hora
                  </Box>
                }
                secondary={dayjs(agendamento.scheduledAt).format(
                  'DD/MM/YYYY [às] HH:mm',
                )}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'textSecondary',
                }}
                secondaryTypographyProps={{
                  color: 'textPrimary',
                  title: agendamento.scheduledAt,
                }}
                style={{ margin: 0 }}
              />
            </Grid>

            <Grid item xs={3}>
              <ListItemText
                primary="Equipe"
                secondary={agendamento.team.name}
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'textSecondary',
                }}
                secondaryTypographyProps={{
                  color: 'textPrimary',
                  title: agendamento.team.name,
                }}
                style={{ margin: 0 }}
              />
            </Grid>

            <Grid item xs={5}>
              <ListItemText
                primary="Canal"
                secondary={
                  <Box display="flex" gridGap={4}>
                    <Box component="span" style={{ marginTop: 1 }}>
                      {getIconTipoCanal({
                        tipoCanal: agendamento.channel.type,
                        size: 16,
                        isModoHibrido: agendamento.channel.isHybridMode,
                      })}
                    </Box>
                    {agendamento.channel.name}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'textSecondary',
                }}
                secondaryTypographyProps={{
                  color: 'textPrimary',
                  title: agendamento.channel.name,
                  component: 'div',
                }}
                style={{ margin: 0 }}
              />
            </Grid>
          </Grid>
        </Box>

        <ListItemText
          primary="Comentário"
          secondary={agendamento.comment || '-'}
          primaryTypographyProps={{
            className: classes.sectionTitle,
          }}
          style={{ margin: 0 }}
        />

        {agendamento.observation && (
          <Box className={classes.observationBox}>
            <ListItemText
              primary="Ocorrência"
              secondary={agendamento.observation}
              primaryTypographyProps={{
                className: classes.observationTitle,
              }}
              secondaryTypographyProps={{
                color: 'textPrimary',
              }}
              style={{ margin: 0 }}
            />
          </Box>
        )}
      </DialogContent>

      {agendamento.status !== AgendamentoStatusEnum.CANCELADO && (
        <DialogActions className={classes.dialogActions}>
          <Button
            variant="text"
            style={{ color: theme.palette.error.main }}
            onClick={e => handleCancel(e, agendamento.id)}
          >
            Cancelar agendamento
          </Button>

          <Box className={classes.actionsRight}>
            <Button variant="text" onClick={onClose}>
              Fechar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditarAgendamento}
            >
              Editar
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
